import { useState, useEffect, Suspense } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Box, Hexagon, CornerDownRight, ShoppingCart, Save, Info, Thermometer, Sun, Lightbulb, Wind, Gauge } from "lucide-react";
import { Button } from "../components/ui/button";
import { Slider } from "../components/ui/slider";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import EnclosureViewer from "../components/EnclosureViewer";
import ARViewButton from "../components/ARViewButton";
import ShareButton from "../components/ShareButton";
import ProductReviews from "../components/ProductReviews";
import SpeciesWizard from "../components/SpeciesWizard";
import BuildQueueBadge from "../components/BuildQueueBadge";
import KlarnaBadge from "../components/KlarnaBadge";
import BundleUpsell from "../components/BundleUpsell";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WOOD_STAINS = {
  // PINE RANGE
  antique_stripped_pine: { name: "Antique Stripped Pine", hex: "#E8D4A8", category: "pine" },
  golden_pine: { name: "Golden Pine", hex: "#D4A84B", category: "pine" },
  dark_pine: { name: "Dark Pine", hex: "#8B6914", category: "pine" },
  // MAHOGANY RANGE
  brown_mahogany: { name: "Brown Mahogany", hex: "#5C3317", category: "mahogany" },
  very_red_mahogany: { name: "Very Red Mahogany", hex: "#8B2500", category: "mahogany" },
  dark_red_mahogany: { name: "Dark Red Mahogany", hex: "#5C1A0B", category: "mahogany" },
  // OAK RANGE
  light_oak: { name: "Light Oak", hex: "#C9A86C", category: "oak" },
  golden_oak: { name: "Golden Oak", hex: "#B8860B", category: "oak" },
  medium_oak: { name: "Medium Oak", hex: "#A0785A", category: "oak" },
  french_oak: { name: "French Oak", hex: "#8B7355", category: "oak" },
  english_oak: { name: "English Oak", hex: "#6B4423", category: "oak" },
  // WALNUT & ROSEWOOD
  dark_walnut: { name: "Dark Walnut", hex: "#4A3728", category: "walnut" },
  light_rosewood: { name: "Light Rosewood", hex: "#9C6B5C", category: "rosewood" },
  light_sycamore: { name: "Light Sycamore", hex: "#F5DEB3", category: "sycamore" },
  // BLACK RANGE
  black_black: { name: "Black Black", hex: "#1A1A1A", category: "black" },
  black_ebony: { name: "Black Ebony", hex: "#2D2D2D", category: "black" },
  // GREY RANGE
  ironstone_grey: { name: "Ironstone Grey", hex: "#4A4A4A", category: "grey" },
  driftwood_grey: { name: "Driftwood Grey", hex: "#78736E", category: "grey" },
  moonlight_grey: { name: "Moonlight Grey", hex: "#B0B0B0", category: "grey" },
  dovetail_grey: { name: "Dovetail Grey", hex: "#6E6E6E", category: "grey" },
  // WHITE & SPECIAL
  soft_white: { name: "Soft White", hex: "#F5F5F0", category: "white" },
  police_box_blue: { name: "Police Box Blue", hex: "#003B6F", category: "special" },
  christmas_green: { name: "Christmas Green", hex: "#165B33", category: "special" },
  christmas_red: { name: "Christmas Red", hex: "#B31B1B", category: "special" },
  // NATURAL
  natural: { name: "Natural (Unfinished)", hex: "#E3C28D", category: "natural" },
};

const STAIN_CATEGORIES = [
  { id: "natural", name: "Natural" },
  { id: "pine", name: "Pine" },
  { id: "oak", name: "Oak" },
  { id: "mahogany", name: "Mahogany" },
  { id: "walnut", name: "Walnut" },
  { id: "rosewood", name: "Rosewood" },
  { id: "sycamore", name: "Sycamore" },
  { id: "grey", name: "Grey" },
  { id: "black", name: "Black" },
  { id: "white", name: "White" },
  { id: "special", name: "Special" },
];

const SHAPES = [
  { id: "rectangular", name: "Rectangular", icon: Box, description: "Classic design" },
  { id: "corner", name: "Corner (L-Shape)", icon: CornerDownRight, description: "Fits in corners" },
  { id: "hexagonal", name: "Hexagonal", icon: Hexagon, description: "Eye-catching display" },
];

const HEATING_MAT_SIZES = [
  { id: "small", name: "Small (6\"×8\")", maxWidth: 3 },
  { id: "medium", name: "Medium (8\"×12\")", maxWidth: 5 },
  { id: "large", name: "Large (12\"×18\")", maxWidth: 8 },
];

const UV_TUBE_SIZES = [
  { id: "18inch", name: "18\" (45cm)", minWidth: 2 },
  { id: "24inch", name: "24\" (60cm)", minWidth: 3 },
  { id: "36inch", name: "36\" (90cm)", minWidth: 4 },
  { id: "48inch", name: "48\" (120cm)", minWidth: 5 },
];

const VENTILATION_OPTIONS = [
  { id: "basic", name: "Basic", description: "Side vents", price: "£12.99" },
  { id: "standard", name: "Standard", description: "Top & side vents", price: "£24.99" },
  { id: "premium", name: "Premium", description: "Adjustable airflow", price: "£44.99" },
];

const THERMOSTAT_OPTIONS = [
  { id: "basic", name: "On/Off", description: "Simple control", price: "£24.99" },
  { id: "dimming", name: "Dimming", description: "Gradual heat", price: "£44.99" },
  { id: "pulse", name: "Pulse Prop.", description: "Precise temp", price: "£64.99" },
];

const ConfiguratorPage = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const { addToCart } = useCart();
  
  const [config, setConfig] = useState({
    shape: "rectangular",
    width: 4,
    height: 4,
    depth: 2,
    stain_color: "natural",
    glass_thickness: 6,
    equipment: {
      heating_mat: null,
      heat_bulb_holders: 0,
      uv_tube_holder: null,
      ventilation: null,
      thermostat: null,
    },
  });
  
  const [selectedCategory, setSelectedCategory] = useState("all");
  
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);
  const [selectedSpeciesId, setSelectedSpeciesId] = useState(null);

  // Hydrate config from URL params on mount (used by Share-this-build deep links).
  const [searchParams] = useSearchParams();
  useEffect(() => {
    const hydration = {};
    const shape = searchParams.get("shape");
    const width = searchParams.get("width") || searchParams.get("w");
    const height = searchParams.get("height") || searchParams.get("h");
    const depth = searchParams.get("depth") || searchParams.get("d");
    const stain = searchParams.get("stain");
    const glass = searchParams.get("glass");
    if (shape && ["rectangular", "corner", "hexagonal"].includes(shape)) hydration.shape = shape;
    if (width) hydration.width = Math.max(2, Math.min(8, Number(width)));
    if (height) hydration.height = Math.max(2, Math.min(8, Number(height)));
    if (depth) hydration.depth = Math.max(2, Math.min(4, Number(depth)));
    if (stain && WOOD_STAINS[stain]) hydration.stain_color = stain;
    if (glass && [4, 6, 8, 10].includes(Number(glass))) hydration.glass_thickness = Number(glass);
    if (Object.keys(hydration).length > 0) {
      setConfig((prev) => ({ ...prev, ...hydration }));
      toast.success("Loaded shared build configuration");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    calculatePrice();
  }, [config]);

  const calculatePrice = async () => {
    try {
      const response = await axios.post(`${API}/pricing/calculate`, config);
      setPricing(response.data);
    } catch (error) {
      console.error("Pricing error:", error);
    }
  };

  const handleAddToCart = async () => {
    if (!user) {
      toast.error("Please login to add items to cart");
      navigate("/login");
      return;
    }
    
    setLoading(true);
    try {
      await addToCart(config, 1);
      toast.success("Added to cart!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add to cart");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveConfig = async () => {
    if (!user) {
      toast.error("Please login to save configurations");
      navigate("/login");
      return;
    }
    
    setSavingConfig(true);
    try {
      await axios.post(
        `${API}/configurations/save?name=${encodeURIComponent(`${config.shape} ${config.width}x${config.height}x${config.depth}`)}`,
        config,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Configuration saved!");
    } catch (error) {
      toast.error("Failed to save configuration");
    } finally {
      setSavingConfig(false);
    }
  };

  const handleWizardApply = (rec) => {
    setConfig((prev) => ({
      ...prev,
      shape: rec.shape || "rectangular",
      width: rec.width,
      height: rec.height,
      depth: rec.depth,
      glass_thickness: rec.glass_thickness,
    }));
    setSelectedSpeciesId(rec.speciesId || null);
    toast.success(`Applied recommended setup for ${rec.speciesName}`);
  };

  return (
    <div className="min-h-screen pt-16 bg-[#0A0A0A]">
      <div className="flex flex-col lg:flex-row lg:h-[calc(100vh-4rem)]">
        {/* 3D Viewer */}
        <div className="h-[50vh] lg:h-auto lg:flex-1 relative" data-testid="configurator-viewer">
          <Suspense fallback={
            <div className="w-full h-full flex items-center justify-center">
              <div className="spinner" />
            </div>
          }>
            <EnclosureViewer config={config} />
          </Suspense>
          
          {/* Dimension badges */}
          <div className="absolute top-4 left-4 flex flex-wrap gap-2">
            <span className="dimension-badge bg-[#121212]/80 backdrop-blur px-3 py-1 text-[#E07A5F] text-sm border border-white/10">
              {config.width}ft × {config.height}ft × {config.depth}ft
            </span>
            <span className="dimension-badge bg-[#121212]/80 backdrop-blur px-3 py-1 text-[#F4F1DE]/60 text-sm border border-white/10">
              {SHAPES.find(s => s.id === config.shape)?.name}
            </span>
            {pricing?.door_type === "hinged_double" && (
              <span className="dimension-badge bg-[#E07A5F]/20 backdrop-blur px-3 py-1 text-[#E07A5F] text-sm border border-[#E07A5F]/30" data-testid="door-type-badge">
                Hinged Double Doors
              </span>
            )}
            {pricing?.door_type === "hinged_single" && (
              <span className="dimension-badge bg-[#E07A5F]/20 backdrop-blur px-3 py-1 text-[#E07A5F] text-sm border border-[#E07A5F]/30" data-testid="door-type-badge">
                Single Hinged Door
              </span>
            )}
          </div>
        </div>

        {/* Control Panel */}
        <div className="w-full lg:w-[400px] glass-panel p-6 overflow-y-auto" data-testid="configurator-controls">
          {/* Build queue badge */}
          <BuildQueueBadge />

          {/* Species Wizard */}
          <div className="mb-6">
            <SpeciesWizard variant="wood" onApply={handleWizardApply} />
          </div>

          {/* Price Display */}
          <div className="mb-8 p-6 bg-[#0A0A0A] border border-white/10">
            <p className="text-[#F4F1DE]/60 text-sm uppercase tracking-widest mb-2">Total Price (inc. VAT)</p>
            <p className="text-4xl font-bold font-['Space_Grotesk'] text-[#E07A5F] price-update" data-testid="total-price">
              £{pricing?.total?.toFixed(2) || "0.00"}
            </p>
            <KlarnaBadge totalPrice={pricing?.total} />
            {pricing && (
              <div className="mt-4 space-y-1 text-sm text-[#F4F1DE]/50">
                <div className="flex justify-between">
                  <span>Marine Plywood</span>
                  <span>£{pricing.plywood_cost.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Toughened Glass</span>
                  <span>£{pricing.glass_cost.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Wood Stain</span>
                  <span>£{pricing.stain_cost.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Labour</span>
                  <span>£{pricing.labor_cost.toFixed(2)}</span>
                </div>
                {pricing.door_frame_cost > 0 && (
                  <div className="flex justify-between text-[#E07A5F]">
                    <span>
                      {pricing.door_type === "hinged_single"
                        ? "Door Frame (×1)"
                        : "Door Frames (×2)"}
                    </span>
                    <span>£{pricing.door_frame_cost.toFixed(2)}</span>
                  </div>
                )}
                {pricing.support_beam_cost > 0 && (
                  <div className="flex justify-between text-[#E07A5F]">
                    <span>Centre Support Beam</span>
                    <span>£{pricing.support_beam_cost.toFixed(2)}</span>
                  </div>
                )}
                {pricing.equipment_cost > 0 && (
                  <div className="flex justify-between text-[#E07A5F]">
                    <span>Equipment</span>
                    <span>£{pricing.equipment_cost.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between pt-2 border-t border-white/10">
                  <span>Subtotal</span>
                  <span>£{pricing.subtotal?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span>VAT (20%)</span>
                  <span>£{pricing.vat?.toFixed(2)}</span>
                </div>
              </div>
            )}
            {/* Door type indicator */}
            {pricing?.door_type === "hinged_double" && (
              <div className="mt-3 px-3 py-2 bg-[#E07A5F]/10 border border-[#E07A5F]/30 rounded text-xs text-[#E07A5F]" data-testid="door-type-indicator">
                {config.shape === "corner"
                  ? "Corner (L-shape) enclosures use hinged doors on both arms (no sliding glass)."
                  : "Hinged double doors with centre support beam (enclosure exceeds 5ft × 5ft)."}
              </div>
            )}
            {pricing?.door_type === "hinged_single" && (
              <div className="mt-3 px-3 py-2 bg-[#E07A5F]/10 border border-[#E07A5F]/30 rounded text-xs text-[#E07A5F]" data-testid="door-type-indicator">
                Hexagonal enclosures use a single hinged door due to their shape.
              </div>
            )}
            
            {/* What's Included */}
            <div className="mt-4 pt-4 border-t border-white/10" data-testid="wood-whats-included">
              <p className="text-[10px] uppercase tracking-[0.22em] text-[#E07A5F]/80 font-semibold mb-2">
                What's included in every build
              </p>
              <ul className="text-xs text-[#F4F1DE]/70 space-y-1.5 leading-relaxed">
                <li className="flex gap-2"><span className="text-[#E07A5F]">•</span> 18mm marine-grade plywood construction</li>
                <li className="flex gap-2"><span className="text-[#E07A5F]">•</span> Toughened safety glass (6mm standard, 8mm / 10mm upgrades available)</li>
                <li className="flex gap-2"><span className="text-[#E07A5F]">•</span> Reptile-safe water-based stain (Littlefair's)</li>
                <li className="flex gap-2"><span className="text-[#E07A5F]">•</span> Pre-drilled cable entry points & integrated ventilation</li>
                <li className="flex gap-2"><span className="text-[#E07A5F]">•</span> Top & bottom plinths with glass runners or hinged door frame</li>
                <li className="flex gap-2"><span className="text-[#E07A5F]">•</span> Hand-built to order in the UK — 7-10 working day lead time</li>
              </ul>
            </div>

            {/* Share Button */}
            <div className="mt-4 pt-4 border-t border-white/10 flex justify-end">
              <ShareButton config={config} price={pricing?.total} />
            </div>
          </div>

          {/* Bundle Upsell — recommended kit based on species pick */}
          <BundleUpsell selectedSpeciesId={selectedSpeciesId} defaultKit="fossorial_gecko" />

          {/* Shape Selection */}
          <div className="mb-6">
            <Label className="text-[#F4F1DE]/80 mb-3 block">Enclosure Shape</Label>
            <div className="grid grid-cols-3 gap-2">
              {SHAPES.map((shape) => (
                <button
                  key={shape.id}
                  onClick={() => setConfig(prev => ({ ...prev, shape: shape.id }))}
                  className={`shape-option p-4 border text-center ${
                    config.shape === shape.id 
                      ? "selected border-[#E07A5F] bg-[#E07A5F]/10" 
                      : "border-white/10 bg-[#121212]"
                  }`}
                  data-testid={`shape-${shape.id}`}
                >
                  <shape.icon className="w-6 h-6 mx-auto mb-2 text-[#E07A5F]" />
                  <span className="text-xs text-[#F4F1DE]/80">{shape.name}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Dimensions */}
          <div className="space-y-6 mb-6">
            <div>
              <div className="flex justify-between mb-2">
                <Label className="text-[#F4F1DE]/80">Width</Label>
                <span className="text-[#E07A5F] font-mono">{config.width}ft</span>
              </div>
              <Slider
                value={[config.width]}
                onValueChange={([value]) => setConfig(prev => ({ ...prev, width: value }))}
                min={2}
                max={8}
                step={0.5}
                className="py-2"
                data-testid="slider-width"
              />
            </div>
            
            <div>
              <div className="flex justify-between mb-2">
                <Label className="text-[#F4F1DE]/80">Height</Label>
                <span className="text-[#E07A5F] font-mono">{config.height}ft</span>
              </div>
              <Slider
                value={[config.height]}
                onValueChange={([value]) => setConfig(prev => ({ ...prev, height: value }))}
                min={2}
                max={8}
                step={0.5}
                className="py-2"
                data-testid="slider-height"
              />
            </div>
            
            <div>
              <div className="flex justify-between mb-2">
                <Label className="text-[#F4F1DE]/80">Depth</Label>
                <span className="text-[#E07A5F] font-mono">{config.depth}ft</span>
              </div>
              <Slider
                value={[config.depth]}
                onValueChange={([value]) => setConfig(prev => ({ ...prev, depth: value }))}
                min={2}
                max={4}
                step={0.5}
                className="py-2"
                data-testid="slider-depth"
              />
            </div>
          </div>

          {/* Glass Thickness */}
          <div className="mb-6 pt-2">
            <Label className="text-[#F4F1DE]/80 mb-3 block">Glass Thickness</Label>
            <div className="grid grid-cols-3 gap-2">
              {[
                { mm: 6, tag: "Standard", blurb: "Geckos, corn snakes, bearded dragons" },
                { mm: 8, tag: "Upgrade", blurb: "Monitors, tegus, larger constrictors" },
                { mm: 10, tag: "Upgrade", blurb: "Heavy-duty: iguanas, large monitors" },
              ].map(({ mm, tag, blurb }) => (
                <button
                  key={mm}
                  onClick={() => setConfig(prev => ({ ...prev, glass_thickness: mm }))}
                  className={`py-2 px-2 border text-center text-sm relative flex flex-col gap-1 items-center justify-start ${
                    config.glass_thickness === mm
                      ? "border-[#E07A5F] bg-[#E07A5F]/10 text-[#E07A5F]"
                      : "border-white/10 bg-[#121212] text-[#F4F1DE]/60"
                  }`}
                  data-testid={`glass-${mm}mm`}
                >
                  <span className="font-semibold">{mm}mm</span>
                  <span className={`text-[10px] leading-tight ${
                    config.glass_thickness === mm ? "text-[#E07A5F]/80" : "text-[#F4F1DE]/40"
                  }`}>
                    {blurb}
                  </span>
                  <span
                    className={`absolute -top-2 left-1/2 -translate-x-1/2 text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded ${
                      tag === "Standard" ? "bg-[#E07A5F] text-white" : "bg-amber-500/80 text-black"
                    }`}
                  >
                    {tag}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Stain Colors */}
          <div className="mb-6">
            <Label className="text-[#F4F1DE]/80 mb-3 block">Wood Stain (Reptile Safe Water Based - 25 Colours)</Label>
            
            {/* Category Filter */}
            <div className="flex flex-wrap gap-1 mb-3">
              <button
                onClick={() => setSelectedCategory("all")}
                className={`px-2 py-1 text-xs border ${
                  selectedCategory === "all"
                    ? "border-[#E07A5F] bg-[#E07A5F]/10 text-[#E07A5F]"
                    : "border-white/10 bg-[#121212] text-[#F4F1DE]/50"
                }`}
              >
                All
              </button>
              {STAIN_CATEGORIES.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setSelectedCategory(cat.id)}
                  className={`px-2 py-1 text-xs border ${
                    selectedCategory === cat.id
                      ? "border-[#E07A5F] bg-[#E07A5F]/10 text-[#E07A5F]"
                      : "border-white/10 bg-[#121212] text-[#F4F1DE]/50"
                  }`}
                >
                  {cat.name}
                </button>
              ))}
            </div>
            
            {/* Color Swatches */}
            <div className="grid grid-cols-8 gap-2">
              {Object.entries(WOOD_STAINS)
                .filter(([_, stain]) => selectedCategory === "all" || stain.category === selectedCategory)
                .map(([key, stain]) => (
                <button
                  key={key}
                  onClick={() => setConfig(prev => ({ ...prev, stain_color: key }))}
                  className={`stain-swatch ${config.stain_color === key ? "selected" : ""}`}
                  style={{ backgroundColor: stain.hex }}
                  title={stain.name}
                  data-testid={`stain-${key}`}
                />
              ))}
            </div>
            <p className="text-xs text-[#F4F1DE]/40 mt-2">
              Selected: {WOOD_STAINS[config.stain_color]?.name}
            </p>
          </div>

          {/* Equipment Section */}
          <div className="mb-8 p-4 bg-[#0A0A0A] border border-white/10">
            <h3 className="text-lg font-bold font-['Space_Grotesk'] text-[#F4F1DE] mb-4 flex items-center gap-2">
              <Thermometer className="w-5 h-5 text-[#E07A5F]" />
              Heating & Lighting
            </h3>
            
            {/* Heating Mat */}
            <div className="mb-4">
              <Label className="text-[#F4F1DE]/80 mb-2 block text-sm flex items-center gap-2">
                <Thermometer className="w-4 h-4" />
                Heating Mat
              </Label>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setConfig(prev => ({ 
                    ...prev, 
                    equipment: { ...prev.equipment, heating_mat: null } 
                  }))}
                  className={`px-3 py-1.5 text-xs border ${
                    !config.equipment.heating_mat 
                      ? "border-[#E07A5F] bg-[#E07A5F]/10 text-[#E07A5F]" 
                      : "border-white/10 bg-[#121212] text-[#F4F1DE]/60"
                  }`}
                  data-testid="heating-mat-none"
                >
                  None
                </button>
                {HEATING_MAT_SIZES.filter(s => config.width <= s.maxWidth).map((size) => (
                  <button
                    key={size.id}
                    onClick={() => setConfig(prev => ({ 
                      ...prev, 
                      equipment: { ...prev.equipment, heating_mat: size.id } 
                    }))}
                    className={`px-3 py-1.5 text-xs border ${
                      config.equipment.heating_mat === size.id 
                        ? "border-[#E07A5F] bg-[#E07A5F]/10 text-[#E07A5F]" 
                        : "border-white/10 bg-[#121212] text-[#F4F1DE]/60"
                    }`}
                    data-testid={`heating-mat-${size.id}`}
                  >
                    {size.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Heat Bulb Holders */}
            <div className="mb-4">
              <Label className="text-[#F4F1DE]/80 mb-2 block text-sm flex items-center gap-2">
                <Lightbulb className="w-4 h-4" />
                Heat Bulb Holders (Ceramic E27)
              </Label>
              <div className="flex gap-2">
                {[0, 1, 2, 3, 4].map((num) => (
                  <button
                    key={num}
                    onClick={() => setConfig(prev => ({ 
                      ...prev, 
                      equipment: { ...prev.equipment, heat_bulb_holders: num } 
                    }))}
                    className={`w-10 h-10 text-sm border ${
                      config.equipment.heat_bulb_holders === num 
                        ? "border-[#E07A5F] bg-[#E07A5F]/10 text-[#E07A5F]" 
                        : "border-white/10 bg-[#121212] text-[#F4F1DE]/60"
                    }`}
                    data-testid={`bulb-holders-${num}`}
                  >
                    {num}
                  </button>
                ))}
              </div>
            </div>

            {/* UV Tube Holder */}
            <div>
              <Label className="text-[#F4F1DE]/80 mb-2 block text-sm flex items-center gap-2">
                <Sun className="w-4 h-4" />
                UV Tube Holder (T8/T5)
              </Label>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setConfig(prev => ({ 
                    ...prev, 
                    equipment: { ...prev.equipment, uv_tube_holder: null } 
                  }))}
                  className={`px-3 py-1.5 text-xs border ${
                    !config.equipment.uv_tube_holder 
                      ? "border-[#E07A5F] bg-[#E07A5F]/10 text-[#E07A5F]" 
                      : "border-white/10 bg-[#121212] text-[#F4F1DE]/60"
                  }`}
                  data-testid="uv-tube-none"
                >
                  None
                </button>
                {UV_TUBE_SIZES.filter(s => config.width >= s.minWidth).map((size) => (
                  <button
                    key={size.id}
                    onClick={() => setConfig(prev => ({ 
                      ...prev, 
                      equipment: { ...prev.equipment, uv_tube_holder: size.id } 
                    }))}
                    className={`px-3 py-1.5 text-xs border ${
                      config.equipment.uv_tube_holder === size.id 
                        ? "border-[#E07A5F] bg-[#E07A5F]/10 text-[#E07A5F]" 
                        : "border-white/10 bg-[#121212] text-[#F4F1DE]/60"
                    }`}
                    data-testid={`uv-tube-${size.id}`}
                  >
                    {size.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Ventilation */}
            <div className="mb-4">
              <Label className="text-[#F4F1DE]/80 mb-2 block text-sm flex items-center gap-2">
                <Wind className="w-4 h-4" />
                Ventilation System
              </Label>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setConfig(prev => ({ 
                    ...prev, 
                    equipment: { ...prev.equipment, ventilation: null } 
                  }))}
                  className={`px-3 py-1.5 text-xs border ${
                    !config.equipment.ventilation 
                      ? "border-[#E07A5F] bg-[#E07A5F]/10 text-[#E07A5F]" 
                      : "border-white/10 bg-[#121212] text-[#F4F1DE]/60"
                  }`}
                  data-testid="ventilation-none"
                >
                  None
                </button>
                {VENTILATION_OPTIONS.map((opt) => (
                  <button
                    key={opt.id}
                    onClick={() => setConfig(prev => ({ 
                      ...prev, 
                      equipment: { ...prev.equipment, ventilation: opt.id } 
                    }))}
                    className={`px-3 py-1.5 text-xs border ${
                      config.equipment.ventilation === opt.id 
                        ? "border-[#E07A5F] bg-[#E07A5F]/10 text-[#E07A5F]" 
                        : "border-white/10 bg-[#121212] text-[#F4F1DE]/60"
                    }`}
                    title={opt.description}
                    data-testid={`ventilation-${opt.id}`}
                  >
                    {opt.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Thermostat */}
            <div>
              <Label className="text-[#F4F1DE]/80 mb-2 block text-sm flex items-center gap-2">
                <Gauge className="w-4 h-4" />
                Thermostat Control
              </Label>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setConfig(prev => ({ 
                    ...prev, 
                    equipment: { ...prev.equipment, thermostat: null } 
                  }))}
                  className={`px-3 py-1.5 text-xs border ${
                    !config.equipment.thermostat 
                      ? "border-[#E07A5F] bg-[#E07A5F]/10 text-[#E07A5F]" 
                      : "border-white/10 bg-[#121212] text-[#F4F1DE]/60"
                  }`}
                  data-testid="thermostat-none"
                >
                  None
                </button>
                {THERMOSTAT_OPTIONS.map((opt) => (
                  <button
                    key={opt.id}
                    onClick={() => setConfig(prev => ({ 
                      ...prev, 
                      equipment: { ...prev.equipment, thermostat: opt.id } 
                    }))}
                    className={`px-3 py-1.5 text-xs border ${
                      config.equipment.thermostat === opt.id 
                        ? "border-[#E07A5F] bg-[#E07A5F]/10 text-[#E07A5F]" 
                        : "border-white/10 bg-[#121212] text-[#F4F1DE]/60"
                    }`}
                    title={opt.description}
                    data-testid={`thermostat-${opt.id}`}
                  >
                    {opt.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Equipment cost breakdown */}
            {pricing?.equipment_cost > 0 && (
              <div className="mt-4 pt-3 border-t border-white/10 text-xs text-[#F4F1DE]/50">
                <p className="font-medium text-[#E07A5F] mb-1">Equipment Added:</p>
                {pricing.equipment_details?.heating_mat && (
                  <div className="flex justify-between">
                    <span>Heating Mat ({pricing.equipment_details.heating_mat.size})</span>
                    <span>£{pricing.equipment_details.heating_mat.price.toFixed(2)}</span>
                  </div>
                )}
                {pricing.equipment_details?.heat_bulb_holders && (
                  <div className="flex justify-between">
                    <span>Heat Bulb Holders ×{pricing.equipment_details.heat_bulb_holders.quantity}</span>
                    <span>£{pricing.equipment_details.heat_bulb_holders.total.toFixed(2)}</span>
                  </div>
                )}
                {pricing.equipment_details?.uv_tube_holder && (
                  <div className="flex justify-between">
                    <span>UV Tube ({pricing.equipment_details.uv_tube_holder.size})</span>
                    <span>£{pricing.equipment_details.uv_tube_holder.price.toFixed(2)}</span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="space-y-3">
            <Button
              onClick={handleAddToCart}
              disabled={loading}
              className="w-full bg-[#E07A5F] hover:bg-[#D06A4F] text-white py-6 font-bold uppercase tracking-widest btn-glow"
              data-testid="add-to-cart-btn"
            >
              {loading ? (
                <span className="spinner mr-2" />
              ) : (
                <ShoppingCart className="w-5 h-5 mr-2" />
              )}
              Add to Cart
            </Button>
            
            <div className="grid grid-cols-2 gap-3">
              <Button
                onClick={handleSaveConfig}
                disabled={savingConfig}
                variant="outline"
                className="border-white/20 hover:border-[#E07A5F]/50 text-[#F4F1DE] py-6"
                data-testid="save-config-btn"
              >
                <Save className="w-5 h-5 mr-2" />
                Save Config
              </Button>
              
              <ARViewButton config={config} />
            </div>
          </div>

          {/* Info */}
          <div className="mt-6 p-4 bg-[#0A0A0A] border border-white/10 text-xs text-[#F4F1DE]/50">
            <div className="flex items-start gap-2">
              <Info className="w-4 h-4 mt-0.5 text-[#E07A5F]" />
              <p>
                Prices based on current UK material costs. Marine plywood BS1088, 
                toughened safety glass, and reptile safe water based stains.
              </p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Customer Reviews Section */}
      <div className="max-w-7xl mx-auto px-4 mt-12 pb-12">
        <ProductReviews productType="wood" />
      </div>
    </div>
  );
};

export default ConfiguratorPage;
